export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export class ApiClient {
  private baseUrl: string;
  private authBaseUrl: string;
  constructor(baseUrl?: string, authBaseUrl?: string) {
    this.baseUrl = (baseUrl || process.env.REACT_APP_API_BASE || 'http://localhost:3002').toString();
    // Allow overriding auth base URL per feature; default to REACT_APP_API_BASE_AUTH or baseUrl
    this.authBaseUrl = (authBaseUrl || process.env.REACT_APP_API_BASE_AUTH || this.baseUrl).toString();
  }

  private getAccessToken() {
    return localStorage.getItem('accessToken') || localStorage.getItem('token');
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) return null;
    try {
      const res = await fetch(this.authBaseUrl + '/admin/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new Error(data?.message || res.statusText || 'Refresh failed');
      const newAccessToken = data?.accessToken ?? data?.data?.accessToken ?? data?.token ?? data?.data?.token;
      const newRefreshToken = data?.refreshToken ?? data?.data?.refreshToken;
      if (newAccessToken) {
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('token', newAccessToken); // backward compatibility
      }
      if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken);
      return newAccessToken || null;
    } catch (e) {
      // Clear potentially invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      return null;
    }
  }

  async request<T>(path: string, method: HttpMethod = 'GET', body?: any): Promise<T> {
    const doFetch = async (authToken?: string) => {
      const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
      const headers: Record<string, string> = {};
      if (!isForm) headers['Content-Type'] = 'application/json';
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const res = await fetch(this.baseUrl + path, {
        method,
        headers,
        body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
      });
      return res;
    };

    let token = this.getAccessToken();
    let res = await doFetch(token || undefined);

    if (res.status === 401) {
      const newToken = await this.refreshAccessToken();
      if (newToken) {
        token = newToken;
        res = await doFetch(token);
      }
    }

    const text = await res.text();
    const data = text ? JSON.parse(text) : null;
    if (!res.ok) {
      const message = data?.message || res.statusText || 'Request failed';
      throw new Error(message);
    }
    return data as T;
  }

  get<T>(path: string) {
    return this.request<T>(path, 'GET');
  }
  post<T>(path: string, body: any) {
    return this.request<T>(path, 'POST', body);
  }
  put<T>(path: string, body: any) {
    return this.request<T>(path, 'PUT', body);
  }
  delete<T>(path: string) {
    return this.request<T>(path, 'DELETE');
  }
}

export const api = new ApiClient();
